import {Card, Divider, Row, Col, Layout, Switch, Typography} from "antd";
import React, { useState, useRef, useEffect, useMemo} from "react";
import {Button} from "@mui/material";
import { EditRounded } from "@mui/icons-material";
import {useScreenshot} from "use-react-screenshot";

import NivoBarChart from "../../charts/nivo/NivoBarChart";
import Screenshot from "../../components/Screenshot";
import StockChart from "../../charts/StockChart";
import AllocationHistoryTable from "./components/AllocationHistoryTable";
import ChartTypeMenu from "./components/ChartTypeMenu";
import TransposedHistoryTable from "./components/TransposedHistoryTable";
import MultiStockChart from "../../SuperComponents/StockCharts/MultiStockChart";
import {transformSeriesForAnychart} from "./utils";
import {useSnapshot} from "../../components/Snapshot/useSnapshot";
import useTickerStyles from "../../components/Snapshot/useTickerStyles";


const ChartToolbar = ({chartType, setChartType, toggleShowSider}) => {
    return (
        <Row justify={'space-between'} className={'px-6'} align={'middle'}>
            {chartType==='table' && (
                <Button
                    size={'small'}
                    onClick={toggleShowSider}
                    variant={'outlined'}
                >
                   <EditRounded fontSize={'small'}/>
                </Button>
            )}
            <ChartTypeMenu {...{chartType, setChartType}} />
        </Row>
    )
}



const transformForStyleSeries = (portfolioSeries) => {
    const sortedSnapshots = [];
    for (let k in portfolioSeries) {
        sortedSnapshots.push({ k: k, v: portfolioSeries[k] });
    }
    sortedSnapshots.sort((a, b) => a.k.localeCompare(b.k));
    return sortedSnapshots.map(x => ({ snapshot: x.v }));
}



const mapTickersToColors = (tickers, colorMapping)=>{
    const newColorMap = {Portfolio: 'var(--green)'};
    for (let ticker in tickers){
        newColorMap[ticker] = colorMapping?.[tickers[ticker]] || 'transparent';
    }

    return newColorMap;
}



/**
 *
 * @param chartData {Array} - Data for the chart where each row is observation for all features
 * @param fieldNames {Array} - Field names for the chart aligned with the # of features
 * @param tableData {Array} - Data for the table
 * @param colorMapping {Object} - Mapping of features to colors
 * @param barPadding {Number} - Padding between bars (Only for views with bar charts)
 * @param barOpacity {Number} - Opacity of bars (Only for views with bar charts)
 * @param chartType {String} - Type of chart to display
 * @param setChartType {Function} - Function to set the chart type
 * @param showSider {Boolean} - Show the chart controller
 * @param toggleShowSider {Function} - Function to toggle the chart controller
 * @param seriesValues
 * @param allowScreenshot
 * @returns {JSX.Element}
 * @constructor
 */
const AllocationContainer = ({chartData, fieldNames, tableData, colorMapping, barPadding, barOpacity,
                              chartType, setChartType, showSider, toggleShowSider, seriesValues, portfolioSeries,
                              allowScreenshot=true, syncRef, callbackOnRangeChange
})=>{
    const [showTable, setShowTable] = useState(true);
    const [showTransposedTable, setShowTransposedTable] = useState(true);
    const [showToolbar, setShowToolbar] = useState(true);
    const containerRef = useRef(null);
    const [image, takeScreenshot] = useScreenshot();
    const { snapshotData, snapshotCallback } = useSnapshot();
    const transformedSeries = useMemo(() => transformForStyleSeries(portfolioSeries), [portfolioSeries]);
    const { tickersToStyles, categoryMapping } = useTickerStyles(transformedSeries, 'tags.style');
    const adjustedColorMap = chartType === 'indices' ? categoryMapping : colorMapping;
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);

      // Dynamically update the height on window resize
      useEffect(() => {
        const handleResize = () => setScreenHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
      }, []);


    const handleChartType = (type) => {
        setChartType(type);
    }
    const transformedBarData = transformSeriesForAnychart(chartData, adjustedColorMap, 'column', barOpacity);
    const [startDate, endDate] = [chartData[0].id, chartData[chartData.length-1].id];

    return (
        <>
            <ChartToolbar
                chartType={chartType}
                setChartType={handleChartType}
                showSider={showSider}
                toggleShowSider={toggleShowSider}
            />
            {chartType==='table' && (
                <Card
                    bordered={false}
                >
                    <Row justify={'space-between'}>
                        <Typography.Title level={4}>Table View by Category</Typography.Title>
                        <Switch
                            checked={showTable}
                            onChange={()=>setShowTable(!showTable)}
                        />
                    </Row>

                    <div style={{height: 400}}>
                        <NivoBarChart
                            data={chartData}
                            keys={fieldNames}
                            mode={'stacked'}
                            precision={0}
                            skipInterval={Math.floor(chartData.length/12)}
                            customColors={colorMapping}
                            barPadding={barPadding}
                            barOpacity={barOpacity}
                            paddingBottom={55}
                            gradientStep={50}
                        />
                    </div>
                    <Divider/>
                    <Typography.Title level={4} style={{textAlign: 'left'}}>Row View by Allocation Period</Typography.Title>
                    {showTable && <TransposedHistoryTable labels={[...fieldNames].map(x=>x.split(' ').slice(0).toString())}
                                                          series={[...tableData]} colorMapping={colorMapping}
                    />}
                    <Divider/>
                    <Typography.Title level={4} style={{textAlign: 'left'}}>Column View by Category </Typography.Title>
                    {showTransposedTable && <AllocationHistoryTable labels={[...fieldNames]} series={[...tableData]} colorMapping={colorMapping}/>}
                </Card>
            )}

            {chartType==='indices'&&(
                <Layout>
                    <Layout.Content ref={containerRef}>
                        <MultiStockChart
                            symbols={['spy', 'vtv', 'qqq']}
                            startDate={startDate}
                            colorMapping={mapTickersToColors(tickersToStyles, categoryMapping)}
                            height={screenHeight/2}
                            initialSeries={[
                                {
                                    symbol: "Portfolio",
                                    data: [...seriesValues].map(x=>{
                                        return {
                                            timestamp: x[0],
                                            'adj close': x[1],
                                            snapshot: portfolioSeries?.[x[0]]
                                        }
                                    }),
                                    callbackOnSnapshot: snapshotCallback,
                                    hasSnapshot: true,
                                    color: 'var(--green)',
                                    stroke: 'green'
                                }]}
                            yMinorGrid={false}
                            stockChartProps={{
                                callbackOnRangeChange: callbackOnRangeChange,
                                showRangeSelector: showToolbar,
                            }}
                            ref={syncRef}


                        />
                        <StockChart
                            dataSeries={[
                                {
                                    plot: 0,
                                    data: transformedBarData,
                                    stack: true,
                                    xGrid: false,
                                    yGrid: false,
                                    yMax: 100
                                }
                            ]}
                            height={screenHeight/4}
                            showRangeSelector={false}
                            ref={syncRef}
                            disableTooltip={true}
                            allowChartInteractions={true}
                        />
                    </Layout.Content>
                </Layout>
            )}
        </>
    )
}
export default AllocationContainer;